/**
 * SubmitTopicMediaForm — inline form on every AI Topic page that lets
 * signed-in members contribute photos, external website references,
 * and video URLs.
 *
 * Photos are uploaded one-by-one via /api/entity/upload (reused from
 * the message board) so the same storage pipeline handles them.
 *
 * Submits to POST /api/ai-topics/{slug}/submit-media.
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Image as ImageIcon, Link as LinkIcon, Video, Loader2, Plus, X, Send, LogIn,
} from "lucide-react";

const MAX_PHOTOS = 6;
const MAX_LINKS  = 5;
const MAX_VIDEOS = 3;
const MAX_BYTES  = 5 * 1024 * 1024;

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith("/") ? "" : "/"}${u}`);

export default function SubmitTopicMediaForm({ slug, onSubmitted }) {
  const { user } = useAuth();
  const [open, setOpen]         = useState(false);
  const [photos, setPhotos]     = useState([]);        // uploaded URLs
  const [caption, setCaption]   = useState("");
  const [links, setLinks]       = useState([{ title: "", url: "" }]);
  const [videos, setVideos]     = useState([{ title: "", url: "" }]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting]   = useState(false);
  const fileRef = useRef(null);

  if (!user) {
    return (
      <div
        className="rounded-2xl border p-4 flex items-center justify-between gap-3 flex-wrap"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
        data-testid="submit-media-signin-nudge"
      >
        <div className="text-sm">
          <div className="font-medium">Have photos, links, or videos to add?</div>
          <div className="text-xs opacity-70 mt-0.5">Sign in to contribute to this topic.</div>
        </div>
        <Link to="/login" className="wofy-btn-primary text-sm" data-testid="submit-media-signin">
          <LogIn className="w-3.5 h-3.5" /> Sign in
        </Link>
      </div>
    );
  }

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (list.length > remaining) toast.error(`You can attach at most ${MAX_PHOTOS} photos.`);
    setUploading(true);
    const uploaded = [];
    for (const f of list.slice(0, remaining)) {
      if (f.size > MAX_BYTES) { toast.error(`"${f.name}" is too large (max 5 MB)`); continue; }
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(f);
      });
      try {
        const r = await http.post(`/entity/upload`, { image: dataUrl });
        if (r.data?.url) uploaded.push(r.data.url);
      } catch { toast.error(`Upload failed for ${f.name}`); }
    }
    setPhotos((prev) => [...prev, ...uploaded].slice(0, MAX_PHOTOS));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e?.preventDefault();
    const cleanLinks  = links.filter(l => l.url.trim());
    const cleanVideos = videos.filter(v => v.url.trim());
    if (!photos.length && !cleanLinks.length && !cleanVideos.length) {
      toast.error("Add at least one photo, website link, or video.");
      return;
    }
    setPosting(true);
    try {
      const r = await http.post(`/ai-topics/${slug}/submit-media`, {
        photo_urls:    photos,
        photo_caption: caption.trim() || null,
        website_links: cleanLinks.map(l => ({ title: l.title.trim() || l.url, url: l.url.trim() })),
        videos:        cleanVideos.map(v => ({ title: v.title.trim() || v.url, url: v.url.trim() })),
      });
      toast.success(`Thanks! ${r.data.photos_added} photos, ${r.data.website_links_added} links, ${r.data.videos_added} videos added.`);
      setPhotos([]); setCaption(""); setLinks([{ title: "", url: "" }]); setVideos([{ title: "", url: "" }]);
      setOpen(false);
      onSubmitted?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit your contribution.");
    } finally { setPosting(false); }
  };

  if (!open) {
    return (
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="wofy-btn-ghost text-sm inline-flex items-center gap-2"
          data-testid="submit-media-open"
        >
          <Plus className="w-4 h-4" /> Add photos, links, or videos to this topic
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-6 rounded-2xl border p-4 sm:p-5 space-y-4"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      data-testid="submit-media-form"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">Contribute to this topic</div>
          <div className="text-xs opacity-70 mt-0.5">Photos, official website references, and educational videos welcome.</div>
        </div>
        <button type="button" onClick={() => setOpen(false)} className="wofy-btn-ghost text-xs" data-testid="submit-media-close">Close</button>
      </div>

      {/* Photos */}
      <div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-2">
          <ImageIcon className="w-3.5 h-3.5" /> Photos ({photos.length}/{MAX_PHOTOS})
        </div>
        <input
          ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/gif"
          multiple className="hidden" onChange={(e) => handleFiles(e.target.files)}
          data-testid="submit-media-file-input"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button" onClick={() => fileRef.current?.click()}
            disabled={uploading || photos.length >= MAX_PHOTOS}
            className="wofy-btn-ghost text-xs"
            data-testid="submit-media-file-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : "Choose photos"}
          </button>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {photos.map((u, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                  <img src={toAbsUrl(u)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute top-0.5 right-0.5 rounded-full bg-black/70 text-white p-0.5"
                    aria-label="Remove photo"
                    data-testid={`submit-media-photo-remove-${i}`}
                  ><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        {photos.length > 0 && (
          <input
            type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="Optional caption for all photos"
            maxLength={200}
            className="mt-2 w-full text-sm rounded-lg border px-3 py-2 bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="submit-media-caption"
          />
        )}
      </div>

      {/* Website links */}
      <div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-2">
          <LinkIcon className="w-3.5 h-3.5" /> Website references
        </div>
        <div className="space-y-2">
          {links.map((l, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
              <input
                type="text" value={l.title} onChange={(e) => setLinks(links.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                placeholder="Title (optional)" maxLength={120}
                className="text-sm rounded-lg border px-3 py-2 bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`submit-media-link-title-${i}`}
              />
              <input
                type="url" value={l.url} onChange={(e) => setLinks(links.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                placeholder="https://example.com/reference" maxLength={400}
                className="text-sm rounded-lg border px-3 py-2 bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`submit-media-link-url-${i}`}
              />
              {links.length > 1 && (
                <button type="button" onClick={() => setLinks(links.filter((_, j) => j !== i))} className="wofy-btn-ghost p-2" aria-label="Remove link">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {links.length < MAX_LINKS && (
            <button
              type="button" onClick={() => setLinks([...links, { title: "", url: "" }])}
              className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1"
              data-testid="submit-media-link-add"
            ><Plus className="w-3.5 h-3.5" /> Add another link</button>
          )}
        </div>
      </div>

      {/* Videos */}
      <div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70 mb-2">
          <Video className="w-3.5 h-3.5" /> Videos (YouTube / Vimeo)
        </div>
        <div className="space-y-2">
          {videos.map((v, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
              <input
                type="text" value={v.title} onChange={(e) => setVideos(videos.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                placeholder="Title (optional)" maxLength={120}
                className="text-sm rounded-lg border px-3 py-2 bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`submit-media-video-title-${i}`}
              />
              <input
                type="url" value={v.url} onChange={(e) => setVideos(videos.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
                placeholder="https://youtube.com/watch?v=…" maxLength={400}
                className="text-sm rounded-lg border px-3 py-2 bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`submit-media-video-url-${i}`}
              />
              {videos.length > 1 && (
                <button type="button" onClick={() => setVideos(videos.filter((_, j) => j !== i))} className="wofy-btn-ghost p-2" aria-label="Remove video">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {videos.length < MAX_VIDEOS && (
            <button
              type="button" onClick={() => setVideos([...videos, { title: "", url: "" }])}
              className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1"
              data-testid="submit-media-video-add"
            ><Plus className="w-3.5 h-3.5" /> Add another video</button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end">
        <button
          type="submit"
          disabled={posting}
          className="wofy-btn-primary text-sm"
          data-testid="submit-media-submit"
        >
          {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {posting ? "Submitting…" : "Submit contribution"}
        </button>
      </div>
    </form>
  );
}
